import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type {
  ConsolidationTheme,
  DiscussionArcPoint,
  PanelRecap,
  RecapConsolidation,
  RecapSection,
  RecapTension,
} from "../../shared/types.ts";
import { normalizeDiscussionArcPoints } from "../../shared/recaps/schema.ts";
import {
  selectVisibleAudienceConsolidation,
  type AudienceSummaryTab,
} from "../lib/audienceConsolidationView.ts";
import { fmtTime } from "../lib/format.ts";
import { EmptyState } from "./EmptyState.tsx";

type AudienceTab = AudienceSummaryTab;

export function AudienceRecapPanel({
  sessionId,
  recaps,
  flow = false,
  tab: controlledTab,
  onTabChange,
  hideTabs = false,
}: {
  sessionId: string;
  recaps: PanelRecap[];
  /**
   * Render as part of the page's own scroll flow (participant view): the
   * panel gives up its inner scroll regions and timeline auto-follow so it
   * never competes with the page scroll owner. Moderator/admin panels keep
   * the default inner-scroll behavior.
   */
  flow?: boolean;
  /** Controlled tab (participant hosts the switch in its toolbar). */
  tab?: AudienceTab;
  onTabChange?: (tab: AudienceTab) => void;
  hideTabs?: boolean;
}) {
  const [internalTab, setInternalTab] = useState<AudienceTab>("timeline");
  const tab = controlledTab ?? internalTab;
  const setTab = (next: AudienceTab) => {
    if (next === tab) return;
    if (onTabChange) onTabChange(next);
    else setInternalTab(next);
  };
  const liveConsolidation = useQuery(
    convexApi.panelRecaps.getConsolidationForAudience,
    { sessionId: sessionId as Id<"sessions"> },
  );
  const displayed = selectVisibleAudienceConsolidation(tab, sessionId, liveConsolidation);

  return (
    <div
      className={`audience-recap-panel${flow ? " audience-recap-flow" : ""}`}
      data-testid="audience-recap-panel"
      data-consolidation-status={
        liveConsolidation === undefined ? "loading" : liveConsolidation === null ? "empty" : "ready"
      }
    >
      {!hideTabs && (
      <div className="recap-tabs audience-recap-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "timeline"}
          className={tab === "timeline" ? "is-active" : ""}
          onClick={() => setTab("timeline")}
        >
          Timeline
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "consolidated"}
          className={tab === "consolidated" ? "is-active" : ""}
          onClick={() => setTab("consolidated")}
        >
          Consolidated
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "themes"}
          className={tab === "themes" ? "is-active" : ""}
          onClick={() => setTab("themes")}
        >
          Themes
        </button>
      </div>
      )}

      <div className="audience-recap-body">
        {tab === "timeline" ? (
          <AudienceTimeline recaps={recaps} follow={!flow} />
        ) : (
          <LiveConsolidatedView
            consolidation={displayed}
            section={tab === "themes" ? "themes" : "arc"}
          />
        )}
      </div>
    </div>
  );
}

interface FlatSection {
  section: RecapSection;
  recapCreatedAt: number;
  ordinalInRecap: number;
}

function AudienceTimeline({ recaps, follow }: { recaps: PanelRecap[]; follow: boolean }) {
  const flat = useMemo(() => {
    const items: FlatSection[] = [];
    for (const recap of recaps) {
      if (!recap.sections) continue;
      for (let i = 0; i < recap.sections.length; i++) {
        items.push({ section: recap.sections[i], recapCreatedAt: recap.createdAt, ordinalInRecap: i });
      }
    }
    items.sort((a, b) =>
      a.section.timeTs - b.section.timeTs
      || a.recapCreatedAt - b.recapCreatedAt
      || a.ordinalInRecap - b.ordinalInRecap,
    );
    return items;
  }, [recaps]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(flat.length);
  const stickRef = useRef(true);

  useLayoutEffect(() => {
    if (follow && flat.length > prevCountRef.current && stickRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevCountRef.current = flat.length;
  }, [flat.length, follow]);

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
  }

  if (flat.length === 0) {
    return (
      <div className="audience-recap-scroll audience-timeline" data-testid="audience-timeline">
        <EmptyState size="sm" testid="audience-timeline-empty">No summaries yet.</EmptyState>
      </div>
    );
  }

  return (
    <div
      className="audience-recap-scroll audience-timeline"
      ref={scrollRef}
      onScroll={handleScroll}
      data-testid="audience-timeline"
    >
      {flat.map((item, idx) => (
        <AudienceSectionCard key={idx} section={item.section} />
      ))}
    </div>
  );
}

function AudienceSectionCard({ section }: { section: RecapSection }) {
  return (
    <div className="recap-section-card" data-testid="recap-section">
      <div className="recap-section-head">
        <span className="recap-section-time">{section.timeLabel}</span>
        <span className="recap-section-speaker">{section.speaker}</span>
      </div>
      <ul className="recap-section-points">
        {section.points.map((point, pi) => (
          <li key={pi}>
            {point.text}
            {point.subpoints.length > 0 && (
              <ul className="recap-subpoints">
                {point.subpoints.map((sub, si) => (
                  <li key={si}>{sub}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function LiveConsolidatedView({
  consolidation,
  section,
}: {
  consolidation: RecapConsolidation | null | undefined;
  section: "arc" | "themes";
}) {
  if (consolidation === undefined) {
    return (
      <div
        className="audience-recap-scroll audience-consolidated audience-consolidated-loading"
        data-testid="audience-consolidated"
        aria-busy="true"
        aria-label="Loading consolidated summary"
      >
        <span className="audience-consolidated-loading-line is-long" aria-hidden="true" />
        <span className="audience-consolidated-loading-line" aria-hidden="true" />
        <span className="audience-consolidated-loading-card" aria-hidden="true" />
      </div>
    );
  }

  if (!consolidation) {
    return (
      <div className="audience-recap-scroll audience-consolidated" data-testid="audience-consolidated">
        <EmptyState size="sm" testid="audience-consolidated-empty">
          {section === "themes" ? "No themes yet." : "No consolidated summary yet."}
        </EmptyState>
      </div>
    );
  }

  const data = consolidation;
  const freshness = data.freshness;

  return (
    <div className="audience-recap-scroll audience-consolidated" data-testid="audience-consolidated">
      {freshness?.behindLatestRecap && (
        <div
          className="audience-consolidated-lag-banner"
          role="status"
          data-testid="consolidated-behind-banner"
        >
          Consolidated summary is catching up to the latest timeline.
        </div>
      )}

      {data.kind === "thematic" ? (
        <AudienceThematicContent narrative={data.narrative} themes={data.themes} section={section} />
      ) : section === "themes" ? (
        // Legacy consolidations predate themes — nothing to split out.
        <EmptyState size="sm" testid="audience-themes-empty">No themes yet.</EmptyState>
      ) : (
        <AudienceLegacyContent sections={data.sections} tensions={data.tensions} />
      )}

      <div className="audience-consolidated-footer">
        Updated {fmtTime(data.consolidatedAt)}
      </div>
    </div>
  );
}

function AudienceThematicContent({
  narrative,
  themes,
  section,
}: {
  narrative: DiscussionArcPoint[];
  themes: ConsolidationTheme[];
  section: "arc" | "themes";
}) {
  // The tab label already names the content, so the split views drop the
  // internal "Discussion arc" / "Key themes" headings.
  if (section === "themes") {
    return (
      <div className="consolidation-themes" data-testid="consolidation-themes">
        {themes.length === 0 && <EmptyState size="sm" testid="audience-themes-empty">No themes yet.</EmptyState>}
        {themes.map((theme, i) => <AudienceThemeCard key={i} theme={theme} />)}
      </div>
    );
  }
  const arc = normalizeDiscussionArcPoints(narrative);
  return (
    <div className="consolidation-narrative" data-testid="consolidation-narrative">
      <ul className="consolidation-narrative-list">
        {arc.map((item, i) => (
          <li key={`${item.point}-${i}`}>
            <span className="consolidation-narrative-point">{item.point}</span>
            {item.subpoints.length > 0 && (
              <ul className="consolidation-narrative-subpoints">
                {item.subpoints.map((subpoint, subpointIndex) => (
                  <li key={`${subpoint}-${subpointIndex}`}>{subpoint}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AudienceThemeCard({ theme }: { theme: ConsolidationTheme }) {
  return (
    <div className="consolidation-theme-card" data-testid="theme-card">
      <h4 className="consolidation-theme-title">{theme.title}</h4>
      <div className="consolidation-theme-speakers">{theme.speakers.join(", ")}</div>
      <p className="consolidation-theme-synthesis">{theme.synthesis}</p>
      {theme.tensions.length > 0 && (
        <div className="consolidation-theme-tensions">
          {theme.tensions.map((tension, ti) => (
            <div key={ti} className="consolidation-theme-tension">
              <span className="consolidation-tension-label">Tension:</span>
              <span>{tension.description}</span>
              <span className="consolidation-tension-speakers">{tension.speakers.join(" vs ")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AudienceLegacyContent({ sections, tensions }: { sections: RecapSection[]; tensions: RecapTension[] }) {
  return (
    <>
      <div className="recap-sections">
        {sections.map((section, idx) => (
          <AudienceSectionCard key={idx} section={section} />
        ))}
      </div>
      {tensions.length > 0 && (
        <div className="recap-tensions" data-testid="tensions-list">
          <h3 className="recap-tensions-heading">Key tensions</h3>
          {tensions.map((tension, idx) => (
            <div key={idx} className="recap-tension-card" data-testid="tension-card">
              <p className="recap-tension-desc">{tension.description}</p>
              <div className="recap-tension-meta">
                <span className="recap-tension-speakers">{tension.speakers.join(" vs ")}</span>
                <span className="recap-tension-time">{tension.timeRange}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
